import type Luminary from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { getVoiceDrivenAsset, isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

/**
 * Error: a voice-driven scene group (talking head or voiceover) is missing the
 * content that drives it — either its voiceover/talking-head base asset or its
 * transcript words. The transcript controls timing, overlays, and captions for
 * the whole group, so an empty one means no narration plays and captions have
 * nothing to sync to. This is always a defect, never an intentional finished
 * state.
 */
export function checkVoiceGroupMissingTranscript(
    luminary: Luminary,
    _getState: GetRootState,
): LintFinding[] {
    const findings: LintFinding[] = [];

    luminary.sceneGroups.forEach((sceneGroup, sceneGroupIndex) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        if (!getVoiceDrivenAsset(sceneGroup)) {
            findings.push({
                check: 'voice_group_missing_transcript',
                severity: 'error',
                sceneGroupIndex,
                message:
                    'This voice-driven scene group has no voiceover/talking-head asset, so there is no narration to play. Add the voiceover or talking-head clip.',
            });
            return;
        }

        const transcript = sceneGroup.originalTranscript;
        const transcriptWords = transcript?.transcriptData?.words || transcript?.words || [];
        if (transcriptWords.length === 0) {
            findings.push({
                check: 'voice_group_missing_transcript',
                severity: 'error',
                sceneGroupIndex,
                message:
                    'This voice-driven scene group has an empty transcript, so there is no narration and captions have nothing to sync to. Add the transcript for this scene group.',
            });
        }
    });

    return findings;
}
